import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IDisposable } from "Global/Base/IDisposable";
import { EventsController } from "Global/Dom/EventsController";
import { ErrorHandler } from "UI/Errors";
import { EnumUtils } from "../../../../Global/Base/Utils/EnumUtils";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IContentBuilder } from "../DataBuilders/IContentBuilder";

const logger = LazyLoadingSectionLogger.create(LoggingSection.sidePanel);

export class SidePanel extends HTMLElement implements IDisposable {
    private _topPanel: HTMLElement;
    private _topContentPanel: HTMLElement;
    private _btnPanel: HTMLElement;
    private _closeBtn: HTMLButtonElement;
    private _contentPanel: HTMLElement;
    private _align: SidePanelAlign;
    private _hasCloseBtn: boolean = true;
    private _eventsController: EventsController = new EventsController();

    private _titleBuilder: IContentBuilder;
    private _contentBuilder: IContentBuilder;
    private _btnPanelBuilder: IContentBuilder;

    public get align(): SidePanelAlign | string {
        return this._align;
    }

    /**
     * Nastavuje zarovnání
     */
    public set align(value: SidePanelAlign | string) {
        this.classList.remove(SidePanelAlign[this._align]);
        if (typeof value === "string")
            this._align = EnumUtils.getEnumValueFromName(SidePanelAlign, value);
        else
            this._align = value;
        this.classList.add(SidePanelAlign[this._align]);
    }

    /**
     * Nastavuje builder pro tlačitka v horním panelu, při vložení builderu je automaticky přepnuto ze zavíracího tlačítka na obsah builderu
     * @param v Builder
     */
    public set btnPanelBuilder(v: IContentBuilder) {
        this._btnPanelBuilder = v;
        this.toggleCloseBtn(false);
    }

    public get btnPanelBuilder(): IContentBuilder {
        return this._btnPanelBuilder;
    }

    public get contentBuilder(): IContentBuilder {
        return this._contentBuilder;
    }

    /**
     * Obsah panelu
     * @param v DataBuilder
     */
    public set contentBuilder(v: IContentBuilder) {
        this._contentBuilder = v;
        this._setContentByBuilder(this._contentPanel, v);
    }

    public get titleBuilder(): IContentBuilder {
        return this._titleBuilder;
    }

    /**
     * Obsah hlavičky
     * @param v
     */
    public set titleBuilder(v: IContentBuilder) {
        this._titleBuilder = v;
        this._setContentByBuilder(this._topContentPanel, v);
    }

    public get visible(): boolean {
        return this.classList.contains("visible");
    }

    public set visible(v: boolean) {
        this.classList.toggle("visible", v);
        //this.callEvent("visibility", { value: v });
    }

    private async _setContentByBuilder(element: HTMLElement, value: IContentBuilder): Promise<void> {
        element.innerHTML = "";
        if (value == null) {
            logger.error("builder not set");
            return;
        }

        try {
            element.appendChild(await value.build());
        } catch (err) {
            logger.error(err);
            ErrorHandler.instance.handleError(err);
        }
    }

    protected static get observedAttributes(): string[] {
        return ["align", "flow"];
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this.classList.add("side-panel");

        this._topPanel = createElement({ classes: "top-panel" });
        this._topContentPanel = createElement({ classes: "top-content-panel" });
        this._btnPanel = createElement({ classes: "top-btn-panel" });
        this._closeBtn = createElement({ classes: "icon close ui-cross-small" }) as HTMLButtonElement;
        Localization.addLocDescData(this._closeBtn, "GUI-668877", "title");//Zavřít panel
        this._contentPanel = createElement({ classes: "content-panel" });

        this.appendChild(this._topPanel);
        this.appendChild(this._contentPanel);
        this._topPanel.appendChild(this._topContentPanel);
        this._topPanel.appendChild(this._btnPanel);
        this._btnPanel.appendChild(this._closeBtn);

        this._eventsController.addListener(this._closeBtn, "click", () => {
            this.visible = false;
        });

        this.align = SidePanelAlign.right;
        this.setAttribute("aria-hidden", "true");
    }

    public show(): void {
        this.visible = true;
        this.setAttribute("aria-hidden", "true");
    }

    public hide(): void {
        this.visible = false;
        this.removeAttribute("aria-hidden");
    }

    /**
     * Přepne mezi zobrazení tlačítka na zavření a hodnotou builderu
     * @param value
     */
    public toggleCloseBtn(value: boolean): void {
        if (value === this._hasCloseBtn)
            return;

        if (value === false) {
            this._hasCloseBtn = false;
            this._setContentByBuilder(this._btnPanel, this._btnPanelBuilder);
        } else if (value === true) {
            this._hasCloseBtn = true;
            this._btnPanel.innerHTML = "";
            this._btnPanel.appendChild(this._closeBtn);
        } else {
            this.toggleCloseBtn(!this._hasCloseBtn);
        }
    }

    public dispose(): void {
        this._eventsController.dispose();
    }
}

export enum SidePanelAlign {
    left,
    right,
    top,
    bottom,
}

registerCustomElement("tsc-SidePanel", SidePanel);
